import cx from "classnames";
import { FC, JSX, ReactNode, useEffect, useId, useRef, useState } from "react";
import { QESButton, QESRadioButton, QESTextInput } from "../qes-standards";

import { DropdownItem, NestingParentDropdownItem } from "./types";
import { expandCollapse } from "../qes-standards/utility/expandCollapse";

interface SingleSelectDropdownProps {
  /**
   * Optional class name(s) added to the div outside the dropdown
   */
  containerClassName?: string;
  /**
   * A string to be rendered next to the Select All button.<br/>
   * This defaults to "Select All"
   */
  customSelectAllText?: string;
  /**
   * A string to be rendered inside the text input/dropdown button.<br/>
   * This defaults to showing item.display if available or item.name
   */
  customSelectInputText?: string;
  /**
   * Disables the text field, dropdown button, and all inputs inside the dropdown.<br/>
   * Does not disable the accordions within the dropdown.
   */
  disabled?: boolean;
  /**
   * Visually hides the label above the dropdown
   */
  hideLabel?: boolean;
  /**
   * Hides the search field within the dropdown
   */
  hideSearch?: boolean;
  /**
   * The list of items rendered within the dropdown
   */
  items: NestingParentDropdownItem[];
  /**
   * The label of the text element outside the dropdown
   */
  label: string | JSX.Element;
  /**
   * The selected child item
   */
  selectedItem: DropdownItem<string>;
  /**
   * A function called on every selection change, returning the selected item
   */
  onSelectionChanged: (item: DropdownItem<string>) => void;
  /**
   * An optional string that appears in a tooltip to the right of the label.<br/>
   * This is intended to display validation requirements to the user.
   */
  validationTooltipContent?: string | JSX.Element;
  /**
   * Required, static, unique string to help QA Automation run tests involving this component
   */
  testId: string;
  /**
   * Display label text in tooltip if text overflows container.
   */
  showTooltipOnOverflow?: boolean;
}

/**
 * A button that summons a dropdown. The dropdown includesa search field for its contents,
 * and a line item for each item passed in through the items prop
 * A change event handler is called on every selection click,
 * so there's no need for a submit button. <br /><br />
 * The parents from the items prop are rendered as closed accordions, which can be opened on click. 
 * The children of each parent are rendered when the accordion is opened. The search box only checks 
 * against children, not parents, and only the state of child items is tracked. 
 * <br /><br />
 * Storybook note: The dotted rectangle is the example's document body, or the area 
 * a user can click in to hide the dropdown.
 */
const SingleSelectNestedDropdown = (props: SingleSelectDropdownProps & { children?: ReactNode }): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectInputText, setSelectInputText] = useState("");
  const [searchText, setSearchText] = useState("");
  const [visibleItems, setVisibleItems] = useState(props.items);
  const inputRef = useRef<HTMLInputElement>(null);
  const uniqueId = useId();

  useEffect(() => {
    setSearchText("");
  }, [isOpen]);

  // Update the dropdown text on selection change
  useEffect(() => {
    if (!props.customSelectInputText) {
      if (!!props.selectedItem) {
        setSelectInputText(`${props.selectedItem.display}`);
      } else {
        setSelectInputText("");
      }
    }
  }, [props.items, props.selectedItem]);

  // filter items by search text
  // we don't care about parent names, matching behavior in the volume reports service area step
  useEffect(() => {
    const newVisibleItems: NestingParentDropdownItem[] = [];
    props.items.forEach((parent) => {
      const itemsToInclude = parent.children.filter((child) => child.name.toLowerCase().includes(searchText.toLowerCase()));
      if (itemsToInclude.length > 0) {
        newVisibleItems.push({
          ...parent,
          children: itemsToInclude,
        })
      }
    })
    setVisibleItems(newVisibleItems);
  }, [props.items, searchText]);

  // close the dropdown if the user clicks outside it
  useEffect(() => {
    if (isOpen) {
      const handleExternalClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest(`#${CSS.escape(uniqueId)}.multiselect-dropdown-container`) === null && isOpen) {
          setIsOpen(false);
          document.body.removeEventListener("click", handleExternalClick);
        }
      };
      document.body.addEventListener("click", handleExternalClick);

      return () => {
        document.body.removeEventListener("click", handleExternalClick);
      };
    }
  }, [isOpen]);

  const updateSelectedItem = (parentKey: string, childKey: string) => {
    const parent = props.items.find((parent) => parent.key === parentKey);

    // don't really need to set selected since it'll get refreshed when the new selectedItem prop comes down
    // and the double update tends to cause a little flicker
    // setSelectedLookup(newSelectedLookup);
    if (!parent) {
      props.onSelectionChanged(null);
    } else {
      props.onSelectionChanged(parent.children.find((child) => child.key === childKey));
    }
  };

  return (
    <div className={cx({
      [props.containerClassName]: !!props.containerClassName,
      "d-inline-block": true,
      "multiselect-dropdown-container multiselect-nested-dropdown": true,
      "position-relative": true,
    })}
      id={uniqueId}
    >
      <QESTextInput
        containerClassName="select-input"
        disabled={props.disabled}
        hideLabel={props.hideLabel}
        qesRef={inputRef}
        inputAppend={
          <QESButton
            qesType="secondary"
            disabled={props.disabled}
            qesTestId="access-reports-admin__add-specialties--input-add"
            onMouseDown={(e) => { e.preventDefault(); inputRef.current.focus(); inputRef.current.click(); }}
            className="select-button"
            aria-label="Toggle Dropdown"
          >
            <i className="fa fa-chevron-down text-dark" />
          </QESButton>
        }

        label={props.label}
        onClick={() => setIsOpen(!isOpen)}
        readOnly={true}
        testId={`search-radio-dropdown__${props.testId}-select-input`}
        validationTooltipContent={props.validationTooltipContent}
        value={!!props.customSelectInputText ? props.customSelectInputText : selectInputText}
      />
      {isOpen && (
        <div
          className={cx({
            "checkbox-dropdown": true,
          })}
        >
          {!props.hideSearch ?
            <QESTextInput
              testId={`search-radio-dropdown__${props.testId}-search-input`}
              disabled={props.disabled}
              hideLabel={true}
              label=""
              placeholder="Search..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              containerClassName="checkbox-dropdown__label"
            /> : null
          }
          <QESRadioButton
            label={"None"}
            className="checkbox-dropdown__checkbox"
            radioContainerClassName="checkbox-dropdown__label"
            checked={!props.selectedItem}
            disabled={props.disabled}
            onChange={() => updateSelectedItem("", "")}
            testId={`singleselect-nested-dropdown__${props.testId}-non`}
            showTooltipOnOverflow={props.showTooltipOnOverflow}
          />
          {/* Updating the key when search is updated forces a rerender and closes all expansions
              Otherwise we have issues with expanded parents being the wrong height
           */}
          <ul key={`${uniqueId}${searchText}`}>
            {visibleItems.map((parent) => {
              return (
                <li key={parent.key}>
                  <ParentAndChildren disabled={props.disabled} parent={parent} updateSelectedItem={updateSelectedItem} selectedItem={props.selectedItem} showTooltipOnOverflow={props.showTooltipOnOverflow} />
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

interface ChildListProps {
  disabled: boolean;
  parent: NestingParentDropdownItem;
  updateSelectedItem: (parentKey: string, childKey: string) => void;
  selectedItem: DropdownItem<string>;
  showTooltipOnOverflow?: boolean;
}

const ParentAndChildren: FC<ChildListProps> = (props: ChildListProps) => {
  const childListRef = useRef<HTMLDivElement>(null);
  const chevronRef = useRef<HTMLElement>(null);
  const { parent, selectedItem, updateSelectedItem } = props;

  const handleOpen = () => {
    chevronRef.current.classList.add("flip");
  }

  const handleClose = () => {
    chevronRef.current.classList.remove("flip");
  }

  return (
    <>
      <div
        onClick={() => expandCollapse(childListRef, handleClose, handleOpen)}
        className="multiselect-nested-dropdown__expander"
      >
        <span className="qes-checkbox__container">{parent.name}</span>
        <i className="fa fa-chevron-down" ref={chevronRef} />
      </div>
      <div ref={childListRef} className="qes-js-closed">
        <ul>
          {parent.children.map((child) => {
            if (!child)
              return;

            return (
              <li key={child.key}>
                <QESRadioButton
                  label={child.name}
                  checked={!!selectedItem && selectedItem.key === child.key}
                  disabled={props.disabled}
                  onChange={() => updateSelectedItem(parent.key, child.key)}
                  testId={`singleselect-nested-dropdown__child--${parent.key}:${child.key}`}
                  showTooltipOnOverflow={props.showTooltipOnOverflow}
                />
              </li>
            )
          })}
        </ul>
      </div>
    </>
  )
}

export default SingleSelectNestedDropdown;
